import React, { useState } from "react";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";

export function escapeCsvField(value) {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToMultiColumnCsv(columns, rows) {
  const header = columns.map((col) => escapeCsvField(col.label)).join(",");
  const body = (rows || []).map((row) =>
    columns.map((col) => escapeCsvField(row[col.key])).join(",")
  );
  return [header, ...body].join("\n");
}

export function triggerCsvDownload(csvString, fileName) {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const ExportCSV = ({
  data = [],
  fileName = "data-export.csv",
  xKey = "province",
  yKey = "count",
  xLabel = "Category",
  yLabel = "Value",
  sortBy = "desc",
  /** When set, export these keys with labels (multi-column). Ignores xKey/yKey for body shape. */
  columns,
  /** When set, rows are loaded on click before export. Return value is used instead of `data`. */
  loadData,
  disabled: disabledFromParent = false,
  buttonText = "Download Full List",
  onError,
}) => {
  const [loading, setLoading] = useState(false);

  const noSyncData = !loadData && (!data || data.length === 0);
  const disabled = disabledFromParent || (loadData && loading) || noSyncData;

  const downloadCSV = async () => {
    if (loadData) setLoading(true);
    try {
      let rows = data;
      if (loadData) {
        rows = await loadData();
      }
      if (!rows || rows.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("No data available for CSV export");
        return;
      }

      let csvString;
      if (columns && columns.length > 0) {
        csvString = rowsToMultiColumnCsv(columns, rows);
      } else {
        let sortedData = [...rows];
        if (sortBy === "desc") {
          sortedData = sortedData.sort((a, b) => b[yKey] - a[yKey]);
        } else if (sortBy === "asc") {
          sortedData = sortedData.sort((a, b) => a[yKey] - b[yKey]);
        }
        csvString = [
          [xLabel, yLabel],
          ...sortedData.map((item) => [
            item[xKey] || "Unknown",
            typeof item[yKey] === "number" ? item[yKey].toLocaleString() : item[yKey],
          ]),
        ]
          .map((row) =>
            row.map((field) => {
              const stringField = String(field);
              if (
                stringField.includes(",") ||
                stringField.includes('"') ||
                stringField.includes("\n")
              ) {
                return `"${stringField.replace(/"/g, '""')}"`;
              }
              return stringField;
            }).join(",")
          )
          .join("\n");
      }

      triggerCsvDownload(csvString, fileName);
    } catch (e) {
      onError?.(e);
      console.error(e);
    } finally {
      if (loadData) setLoading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="secondary"
      startIcon={<Download />}
      onClick={() => void downloadCSV()}
      disabled={disabled}
      size="small"
    >
      {loadData && loading ? "Downloading…" : buttonText}
    </Button>
  );
};

export default ExportCSV;
